import React, { useEffect, useState } from 'react';
import { BlogPost, BlogCategory, BlogService } from '../../Services/Blog/BlogService';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import PageTemplate from 'Components/Templates/Page/Page/PageTemplate';
import { ROUTES } from 'Services/Route/Route.types';
import PageHeader from 'Components/Organisms/PageHeader/PageHeader';
import Breadcrumbs from 'Components/Molecules/Breadcrumbs/Breadcrumbs';
import BreadcrumbItem from 'Components/Atoms/BreadcrumbItem/BreadcrumbItem';
import Container from 'Components/Atoms/Container/Container';
import Button from 'Components/Atoms/Button/Button';
import Notification from 'Components/Atoms/Notification/Notification';
import TableFixed from 'Components/Molecules/Table/TableFixed/TableFixed';
import TableHeader from 'Components/Molecules/TableHeader/TableHeader';
import TableColumn from 'Components/Molecules/TableColumn/TableColumn';
import TableBody from 'Components/Molecules/TableBody/TableBody';
import TableRow from 'Components/Molecules/TableRow/TableRow';
import TableCell from 'Components/Molecules/TableCell/TableCell';
import EditButton from 'Components/Molecules/EditButton/EditButton';
import { faNewspaper, faTags, faPlus, faTrash, faEdit } from '@fortawesome/pro-solid-svg-icons';
import FontAwesomeIcon from 'Components/Atoms/FontAwesomeIcon/FontAwesomeIcon';
import Text from 'Components/Atoms/Text/Text';
import Badge from 'Components/Atoms/Badge/Badge';
import { TableProvider } from 'Services/Table/TableContext';

const BlogList: React.FC = () => {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const blogService = BlogService.getInstance();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const posts = await blogService.getBlogPosts();
      
      console.log('📝 BlogList: Ruwe posts data:', posts);
      
      // API responses zijn al gecorrigeerd in BlogService, direct gebruiken
      setBlogPosts(posts);
    } catch (err) {
      console.error('❌ BlogList: loadData mislukt', err);
      setError(err instanceof Error ? err.message : 'Blog artikelen laden mislukt');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Weet je zeker dat je dit blog artikel wilt verwijderen?')) {
      try {
        await blogService.deleteBlogPost(id);
        setBlogPosts(blogPosts.filter(post => post.id !== id));
      } catch (err) {
        setError('Blog artikel verwijderen mislukt.');
      }
    }
  };

  const getCategoryName = (post: BlogPost): string => {
    // API response bevat category object
    if (post.category) {
      return post.category.name;
    }
    // Fallback voor categories state
    const category = categories.find(cat => cat.id === post.category_id);
    return category ? category.name : 'Geen categorie';
  };

  const formatDate = (dateObj?: any): string => {
    if (!dateObj) return 'Geen datum';
    
    // API response bevat date object: {date: "2025-06-19 09:16:51.000000", timezone_type: 3, timezone: "UTC"}
    if (dateObj.date) {
      return new Date(dateObj.date).toLocaleDateString('nl-NL');
    }
    
    // Als string komt, direct gebruiken
    if (typeof dateObj === 'string') {
      return new Date(dateObj).toLocaleDateString('nl-NL');
    }
    
    return 'Geen datum';
  };

  if (loading) return (
    <PageTemplate>
      <Container padding='vertical'>
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <Text>Laden...</Text>
        </div>
      </Container>
    </PageTemplate>
  );

  if (error) return (
    <PageTemplate>
      <Container padding='vertical'>
        <Notification variant='error'>{error}</Notification>
      </Container>
    </PageTemplate>
  );

  return (
    <TableProvider pageNumber={1} totalPageNumber={1} onPageChange={() => {}}>
      <PageTemplate>
        <Helmet>
          <title>{ ROUTES.BLOG.title }</title>
        </Helmet>
        
        <PageHeader
          breadcrumbs={
            <Breadcrumbs>
              <BreadcrumbItem size='small' path={ROUTES.DASHBOARD.path}>Dashboard</BreadcrumbItem>
              <BreadcrumbItem size='small'>Blog</BreadcrumbItem>
            </Breadcrumbs>
          }
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ 
              width: '48px', 
              height: '48px', 
              backgroundColor: 'transparent', 
              borderRadius: '8px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}>
              <FontAwesomeIcon icon={faNewspaper} size="lg" color="#2563eb" />
            </div>
            <div>
              <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827', margin: 0 }}>
                {blogPosts.length} Blog artikelen
              </h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '8px' }}>
                <Badge variant='success'>
                  {blogPosts.filter(post => post.isPublished).length} Gepubliceerd
                </Badge>
                <Badge variant='warning'>
                  {blogPosts.filter(post => !post.isPublished).length} Concept
                </Badge>
              </div>
            </div>
          </div>
        </PageHeader>

        <Container padding='vertical'>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginBottom: '24px' }}>
            <Button
              variant='border'
              color='neutral-dark'
              size='medium'
              onClick={() => navigate('/blog/categories')}
              iconBefore={<FontAwesomeIcon icon={faTags} />}
            >
              Categorieën
            </Button>
            <Button
              variant='solid'
              color='primary'
              size='medium'
              onClick={() => navigate('/blog/create')}
              iconBefore={<FontAwesomeIcon icon={faPlus} />}
            >
              Nieuw artikel
            </Button>
          </div>

          {blogPosts.length === 0 ? (
            <div style={{ 
              backgroundColor: 'white', 
              borderRadius: '8px', 
              border: '1px solid #e5e7eb', 
              padding: '48px', 
              textAlign: 'center' 
            }}>
              <div style={{ marginBottom: '16px' }}>
                <FontAwesomeIcon icon={faNewspaper} size="2x" color="#9ca3af" />
              </div>
              <Text weight='semibold' color='secondary'>Geen blog artikelen</Text>
              <Text color='secondary'>
                Begin met het maken van je eerste blog artikel.
              </Text>
              <Button
                variant='solid'
                color='primary'
                size='medium'
                onClick={() => navigate('/blog/create')}
                iconBefore={<FontAwesomeIcon icon={faPlus} />}
              >
                Nieuw artikel
              </Button>
            </div>
          ) : (
            <div style={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
              <TableFixed
                header={
                  <TableHeader>
                    <TableColumn>
                      Artikel
                    </TableColumn>
                    <TableColumn>
                      Categorie
                    </TableColumn>
                    <TableColumn>
                      Status
                    </TableColumn>
                    <TableColumn>
                      Aangemaakt
                    </TableColumn>
                    <TableColumn>
                      Bijgewerkt
                    </TableColumn>
                    <TableColumn>
                      Acties
                    </TableColumn>
                  </TableHeader>
                }
              >
                <TableBody>
                  {blogPosts.map(post => (
                    <TableRow key={post.id}>
                      <TableCell>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{ 
                            width: '40px', 
                            height: '40px', 
                            backgroundColor: '#dbeafe', 
                            borderRadius: '6px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center' 
                          }}>
                            {post.imageUrl ? (
                              <img 
                                src={post.imageUrl} 
                                alt={post.title}
                                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '6px' }}
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            ) : (
                              <FontAwesomeIcon icon={faNewspaper} size="sm" color="#2563eb" />
                            )}
                          </div>
                          <div>
                            <a 
                              href={`https://www.growing-people.nl/blogs/${post.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ 
                                textDecoration: 'underline', 
                                color: '#2563eb',
                                cursor: 'pointer',
                                fontWeight: '600'
                              }}
                            >
                              {post.title}
                            </a>
                            {post.excerpt && (
                              <Text color='secondary'>
                                {post.excerpt.length > 60 ? `${post.excerpt.substring(0, 60)}...` : post.excerpt}
                              </Text>
                            )}
                            <Text color='secondary'>Slug: {post.slug}</Text>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant='info'>
                          {getCategoryName(post)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={post.isPublished ? 'success' : 'warning'}>
                          {post.isPublished ? 'Gepubliceerd' : 'Concept'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Text color='secondary'>
                          {formatDate(post.created_at)}
                        </Text>
                      </TableCell>
                      <TableCell>
                        <Text color='secondary'>
                          {formatDate(post.updated_at)}
                        </Text>
                      </TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <Button
                            variant='transparent'
                            size='small'
                            onClick={() => navigate(`/blog/edit/${post.id}`)}
                            iconBefore={<FontAwesomeIcon icon={faEdit} />}
                          >
                            Bewerken
                          </Button>
                          <Button
                            variant='transparent'
                            size='small'
                            color='error'
                            onClick={() => handleDelete(post.id)}
                            iconBefore={<FontAwesomeIcon icon={faTrash} />}
                          >
                            Verwijderen
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </TableFixed>
            </div>
          )}
        </Container>
      </PageTemplate>
    </TableProvider>
  );
};

export default BlogList; 